/**
 * # Time helper
 * General helpers for working with times.
 *
 * @module helpers/time/time.js
 */


/**
 * Check if provided time is valid
 * @param  {String} time
 * @return {Boolean|String}
 */
function isValidTime(time) {
  let timeString = time;

  if (timeString.indexOf(':') === -1) {
    return false;
  }

  // Remove all spaces and periods from the string
  timeString = timeString.replace(/\.|\s/g, '').toLowerCase();

  if (((timeString.indexOf('am') === -1) && (timeString.indexOf('pm') === -1))) {
    // 24-hour clock system
    if (timeString.length !== 5) {
      return false;
    }

    let [hours, minutes] = timeString.split(':');

    if (parseInt(minutes) < 0 || parseInt(minutes) >= 60) {
      return false;
    }

    if (parseInt(hours) >= 0 && parseInt(hours) <= 23) {
      return '24';
    } else {
      return false;
    }
  } else {
    // 12-hour clock system
    if (timeString.length !== 7) {
      return false;
    }

    let [hours, minutes] = timeString.split(':');

    if (parseInt(minutes) < 0 || parseInt(minutes) >= 60) {
      return false;
    }

    if (parseInt(hours) >= 1 && parseInt(hours) <= 12) {
      return '12';
    } else {
      return false;
    }
  }
}


/**
 * Convert 24hr time to 12hr time
 * @param  {String} time
 * @return {String}
 */
function convertTimeTo12Hr(time) {
  let timeString = time.replace(/\.|\s/g, '').toLowerCase();
  const isValid = isValidTime(timeString);

  if (isValid) {
    if (isValid === '24') {
      let [hours, minutes] = timeString.split(':');
      let modifier = 'a.m.';

      hours = Number(hours);

      if (hours >= 12) {
        hours = hours - 12;
        modifier = 'p.m.';
      }

      if (hours === 0) {
        hours = 12;
      }

      hours = hours.toString().padStart(2, '0');
      minutes = minutes.toString().padStart(2, '0');

      return `${hours}:${minutes} ${modifier}`;
    }

    return time;
  }

  return undefined;
}


/**
 * Convert 12hr time to 24hr time
 * @param  {String} time
 * @return {String}
 */
function convertTimeTo24Hr(time) {
  let timeString = time.replace(/\.|\s/g, '').toLowerCase();
  const isValid = isValidTime(timeString);

  if (isValid) {
    if (isValid === '12') {
      let [hours, minutes] = timeString.split(':');
      let modifier;

      if (minutes.includes('am') || minutes.includes('pm')) {
        modifier = minutes.substr(minutes.length - 2);
        minutes = minutes.substr(0, minutes.length - 2);
      }

      if (hours === '12') {
        hours = '00';
      }

      if (modifier === 'pm') {
        hours = parseInt(hours, 10) + 12;
      }

      return `${hours}:${minutes}`;
    }

    return time;
  }

  return undefined;
}


/**
 * Convert minutes since midnight to 12hr or 24hr time convention
 * @param  {String} minutesAfterMidnight
 * @param  {String} convention
 * @return {String}
 */
function convertMinutesToTime(minutesAfterMidnight, convention) {
  let timeInt = parseInt(minutesAfterMidnight);

  // 0 for midnight, 1439 for 11:59 p.m.
  if (isNaN(timeInt) || timeInt > 1439) {
    return undefined;
  }

  let hours = Math.floor(timeInt / 60);
  hours = hours.toString().padStart(2, '0');
  let minutes = (timeInt % 60);
  minutes = minutes.toString().padStart(2, '0');

  if (convention === '12') {
    const timeString = `${hours}:${minutes}`;
    const timeIn12Hr = convertTimeTo12Hr(timeString);

    return timeIn12Hr;
  }

  return `${hours}:${minutes}`;
}


/**
 * Convert 12hr or 24hr time to minutes since midnight
 * @param  {String} time
 * @return {Integer}
 */
function convertTimeToMinutes(time) {
  let timeString = time.replace(/\.|\s/g, '').toLowerCase();
  const isValid = isValidTime(timeString);

  if (isValid) {
    if (isValid === '12') {
      timeString = convertTimeTo24Hr(timeString);
    }

    let [hours, minutes] = timeString.split(':');
    minutes = (Number(hours) * 60) + Number(minutes);

    return minutes;
  }

  return undefined;
}


/**
 * Get the period (am or pm) from a valid time
 * @param  {String} time
 * @return {String}
 */
function getTimePeriod(time) {
  let timeString = time;
  const isValid = isValidTime(timeString);

  if (isValid) {
    if (isValid === '24') {
      timeString = convertTimeTo12Hr(timeString);
    }

    let minutes = timeString.split(':')[1];
    let period;

    if (minutes.includes('a.m.') || minutes.includes('p.m.')) {
      period = minutes.substr(minutes.length - 4);
      return period;
    }

    return undefined;
  }

  return undefined;
}

export {isValidTime, convertTimeTo12Hr, convertTimeTo24Hr, convertMinutesToTime, convertTimeToMinutes, getTimePeriod};
